// The API keys table's columns, each with its help text (DSEC D1, plan section 5), and the matching
// semantics the edit dialog explains (apiv2 developer.openapi.yaml ApiCredentialUpdate).

import type { ApiCredential } from './apiKeyTypes';

export interface ApiKeyColumn {
    id: string;
    label: string;
    help: string;
}

export const API_KEY_COLUMNS: ApiKeyColumn[] = [
    { id: 'name', label: 'Name', help: 'Your name for the key, to tell your keys apart.' },
    { id: 'description', label: 'Description', help: 'What the key is for, and who uses it.' },
    { id: 'scope', label: 'Scope', help: 'Which API the key opens. A key works only for its scope; the scope never changes.' },
    {
        id: 'prefix',
        label: 'Prefix / fingerprint',
        help: 'The key itself is shown once, when it is created. Its first characters and its fingerprint identify it in logs and audit records.',
    },
    { id: 'status', label: 'Status', help: 'Active keys work until they expire. A revoked key never works again.' },
    { id: 'created', label: 'Created', help: 'When the key was created, by whom, and from which IP address.' },
    { id: 'last_used', label: 'Last used', help: 'The last request made with the key, and the IP address it came from.' },
    { id: 'expires', label: 'Expires', help: 'When the key stops working. A rotated key works until its grace period ends.' },
    {
        id: 'legacy_access',
        label: 'Legacy access flags',
        help: 'For import API keys: what the key may read back through import/query and the MCP query tools. Without a flag, import/query answers 403 for that record type.',
    },
    { id: 'allowed_ips', label: 'Allowed IPs', help: 'The IP addresses or networks the key may be used from. Empty: any.' },
    { id: 'user_agent_pattern', label: 'User-agent pattern', help: 'The client (User-Agent header) the key may be used by. Empty: any.' },
    { id: 'access_groups', label: 'Access groups', help: 'The access groups the key acts with. Set by Curated Planet; read-only here.' },
];

/** The edit dialog's help for the two restrictions (owner decision 10-24: CIDR or exact IP; a glob). */
export const ALLOWED_IPS_HELP = 'One per line: an exact IP address (203.0.113.7, 2001:db8::1) or a CIDR network (203.0.113.0/24). Empty: any IP.';
export const USER_AGENT_HELP = 'A pattern for the whole User-Agent header, not case-sensitive: * matches any characters, ? one character (e.g. MyPMS/*). Empty: any client.';

/** Whether the key no longer works (revoked, or past its expiry). */
export const isSpent = (row: ApiCredential, now = Date.now()) => row.status === 'revoked' || (!!row.expires && Date.parse(row.expires) <= now);

/** Partner keys rotate by claiming a new one (backend api_credential_not_rotatable). */
export const isRotatable = (row: ApiCredential, now = Date.now()) => (
    row.status === 'active' && !row.rotated_at && !isSpent(row, now) && row.scope !== 'partner_dev' && !row.partner && !row.in_use
);

/** The allowed-IPs text of the edit dialog -> the PATCH list (one entry per line, commas also split). */
export const parseAllowedIps = (text: string): string[] => text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
